import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Header,
  Icon,
  Container,
  Table,
  Button,
  Loader,
  Message,
  Modal,
  Form,
  Input,
  Dropdown,
  Segment,
  Dimmer,
  Grid,
  Label,
  Confirm,
  Pagination,
  Checkbox,
  List
} from 'semantic-ui-react';
import GlassPageWrapper from '../../components/Layout/GlassPageWrapper';
import GlassTable from '../../components/Common/GlassTable';
import StockClusterCards from './components/StockClusterCards';
import { buildIccidClusterSummary } from './buildIccidClusterSummary';
import { icCidService } from '../../services/icCids/ICcIdService';
import { 
  ICcIdResponse, 
  CreateICcIdRequest, 
  UpdateICcIdRequest, 
  ICcIdFilters,
  ICcIdStockSummaryResponse,
  ICcIdExcelResultResponse,
  ICcIdBatchResultResponse,
  FastchipExcelCancellationResultResponse,
  BatchUpdateMode,
  BatchUpdateICcIdsRequest,
} from '../../types/dtos';
import { DesignSystem, getDefaultButtonStyle, getButtonHoverEffects } from '../../styles/designSystem';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/useToast';
import { contractService } from '../../services/contracts/ContractService';
import { formatCnpj } from '../../services/utils/stringUtils';
import { extractApiErrorMessage, isCanceledError } from '../../services/utils/apiErrorUtils';
import { getContractStatusSemanticColor, getContractStatusText } from '../../utils/contractStatusUtils';
import { FastLinkedContractModal } from '../../components/contracts/FastLinkedContractModal';
import { FastChipMovementHistoryModal } from '../../components/icCids/FastChipMovementHistoryModal';

/**
 * Lista canônica das 27 UFs brasileiras (26 estados + DF) em ordem alfabética por sigla,
 * no formato do Semantic UI Dropdown. Usada nos dropdowns de seleção de estado
 * (criação/edição de ICCID e importação Excel).
 */
const BRAZIL_UF_OPTIONS: { key: string; value: string; text: string }[] = [
  { key: 'AC', value: 'AC', text: 'AC - Acre' },
  { key: 'AL', value: 'AL', text: 'AL - Alagoas' },
  { key: 'AP', value: 'AP', text: 'AP - Amapá' },
  { key: 'AM', value: 'AM', text: 'AM - Amazonas' },
  { key: 'BA', value: 'BA', text: 'BA - Bahia' },
  { key: 'CE', value: 'CE', text: 'CE - Ceará' },
  { key: 'DF', value: 'DF', text: 'DF - Distrito Federal' },
  { key: 'ES', value: 'ES', text: 'ES - Espírito Santo' },
  { key: 'GO', value: 'GO', text: 'GO - Goiás' },
  { key: 'MA', value: 'MA', text: 'MA - Maranhão' },
  { key: 'MT', value: 'MT', text: 'MT - Mato Grosso' },
  { key: 'MS', value: 'MS', text: 'MS - Mato Grosso do Sul' },
  { key: 'MG', value: 'MG', text: 'MG - Minas Gerais' },
  { key: 'PA', value: 'PA', text: 'PA - Pará' },
  { key: 'PB', value: 'PB', text: 'PB - Paraíba' },
  { key: 'PR', value: 'PR', text: 'PR - Paraná' },
  { key: 'PE', value: 'PE', text: 'PE - Pernambuco' },
  { key: 'PI', value: 'PI', text: 'PI - Piauí' },
  { key: 'RJ', value: 'RJ', text: 'RJ - Rio de Janeiro' },
  { key: 'RN', value: 'RN', text: 'RN - Rio Grande do Norte' },
  { key: 'RS', value: 'RS', text: 'RS - Rio Grande do Sul' },
  { key: 'RO', value: 'RO', text: 'RO - Rondônia' },
  { key: 'RR', value: 'RR', text: 'RR - Roraima' },
  { key: 'SC', value: 'SC', text: 'SC - Santa Catarina' },
  { key: 'SP', value: 'SP', text: 'SP - São Paulo' },
  { key: 'SE', value: 'SE', text: 'SE - Sergipe' },
  { key: 'TO', value: 'TO', text: 'TO - Tocantins' },
];

const ICcIdsPage: React.FC = () => {
  const [icCids, setICcIds] = useState<ICcIdResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedICcId, setSelectedICcId] = useState<ICcIdResponse | null>(null);
  const [formData, setFormData] = useState<Partial<CreateICcIdRequest>>({});
  const [isEditMode, setIsEditMode] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [iccidToDelete, setICcIdToDelete] = useState<string | null>(null);
  const [excelModalOpen, setExcelModalOpen] = useState(false);
  const [excelFile, setExcelFile] = useState<File | null>(null);
  const [excelEstados, setExcelEstados] = useState<string[]>([]); // Array de estados
  /** Cluster EAS/TIM (ER | MH) quando PR está na importação Excel */
  const [excelClusterParana, setExcelClusterParana] = useState<string>('');
  const [excelResult, setExcelResult] = useState<ICcIdExcelResultResponse | null>(null);
  const [stockSummary, setStockSummary] = useState<ICcIdStockSummaryResponse | null>(null);
  const [uploadingExcel, setUploadingExcel] = useState(false);
  const [fastchipModalOpen, setFastchipModalOpen] = useState(false);
  const [fastchipFile, setFastchipFile] = useState<File | null>(null);
  const [uploadingFastchip, setUploadingFastchip] = useState(false);
  const [fastchipResult, setFastchipResult] = useState<FastchipExcelCancellationResultResponse | null>(null);
  const [scClusterCard, setScClusterCard] = useState({ total: 0, disponivel: 0, percentual: 0 });

  /** Modal resumido do contrato vinculado (lista FAST / spec ideia). */
  const [contractModalOpen, setContractModalOpen] = useState(false);
  const [contractModalIccid, setContractModalIccid] = useState<ICcIdResponse | null>(null);

  // Multi-seleção e operações em lote
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [batchConfirmOpen, setBatchConfirmOpen] = useState(false);
  const [batchAction, setBatchAction] = useState<'available' | 'unavailable' | 'delete' | null>(null);
  const [batchLoading, setBatchLoading] = useState(false);

  // Modal "Editar selecionados em lote"
  const [batchEditOpen, setBatchEditOpen] = useState(false);
  const [batchEditMode, setBatchEditMode] = useState<BatchUpdateMode>('replace');
  const [batchEditEstados, setBatchEditEstados] = useState<string[]>([]);
  const [batchEditCluster, setBatchEditCluster] = useState<string>('');
  const [batchEditLoading, setBatchEditLoading] = useState(false);
  const [ccIdSearchOptions, setCcIdSearchOptions] = useState<{ key: string; value: string; text: string }[]>([]);
  const [ccIdSearchValues, setCcIdSearchValues] = useState<string[]>([]);
  const [ccIdPartialQuery, setCcIdPartialQuery] = useState('');
  const ccIdSearchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  // Estados para filtros e paginação
  const [filters, setFilters] = useState<ICcIdFilters>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [sortBy, setSortBy] = useState<string>('createdAt');
  const [sortDescending, setSortDescending] = useState(true);
  const [listReloadKey, setListReloadKey] = useState(0);
  const [summaryReloadKey, setSummaryReloadKey] = useState(0);

  /** Modal de histórico append-only FAST (GET `/icCids/{id}/movements`, só admin na rota). */
  const [historyMovementIccid, setHistoryMovementIccid] = useState<ICcIdResponse | null>(null);
  const historyModalOpen = historyMovementIccid !== null;

  const { user } = useAuth();
  const { showSuccess, showError } = useToast();

  // Verificar se o usuário tem permissão de Admin
  const isAdmin = !!(user?.roles && user.roles.includes('Admin'));

  // SC em porEstado; SH legado em `shSpLeftoverBreakdown`; E.R.2 em `rjmgFastBreakdown`.
  const clusterSummary = useMemo(() => buildIccidClusterSummary(stockSummary), [stockSummary]);

  /** Fallback quando a API ainda não envia `paranaFastBreakdown` — card PR único a partir de porEstado. */
  const paranaAggregatedFallback = useMemo(() => {
    const row = stockSummary?.porEstado?.find(e => e.estado?.toUpperCase() === 'PR');
    if (!row) return null;
    return {
      total: row.total,
      disponivel: row.disponivel,
      percentual: row.percentualDisponivel
    };
  }, [stockSummary]);

  const effectiveFilters = useMemo<ICcIdFilters>(() => ({
    ...filters,
    ccId: (ccIdPartialQuery && ccIdPartialQuery.trim()) ? ccIdPartialQuery.trim() : filters.ccId
  }), [filters, ccIdPartialQuery]);

  const refreshList = useCallback(() => {
    setListReloadKey(prev => prev + 1);
  }, []);

  const refreshSummary = useCallback(() => {
    setSummaryReloadKey(prev => prev + 1);
  }, []);

  const refreshAll = useCallback(() => {
    refreshList();
    refreshSummary();
  }, [refreshList, refreshSummary]);

  const closeContractSummaryModal = useCallback(() => {
    setContractModalOpen(false);
    setContractModalIccid(null);
  }, []);

  const openContractSummaryModal = useCallback((iccid: ICcIdResponse) => {
    if (!iccid.contractId) return;
    setContractModalIccid(iccid);
    setContractModalOpen(true);
  }, []);

  useEffect(() => {
    return () => {
      if (ccIdSearchDebounceRef.current) clearTimeout(ccIdSearchDebounceRef.current);
    };
  }, []);

  const loadICcIds = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await icCidService.searchICcIds(
        effectiveFilters,
        currentPage,
        pageSize,
        sortBy,
        sortDescending
      );
      
      setICcIds(response.items);
      setTotalPages(response.totalPages);
      setTotalCount(response.totalCount);
    } catch (err) {
      if (isCanceledError(err)) return;
      const message = extractApiErrorMessage(err, 'Erro ao carregar ICCIDs');
      setError(message);
      showError(message);
    } finally {
      setLoading(false);
    }
  }, [currentPage, effectiveFilters, pageSize, sortBy, sortDescending, listReloadKey, showError]);

  const loadScClusterCardMetrics = useCallback(async () => {
    try {
      // Usa a mesma base de filtro da tabela para garantir consistência do card SC
      const [totalResponse, availableResponse] = await Promise.all([
        icCidService.searchICcIds({ estado: 'SC' }, 1, 1),
        icCidService.searchICcIds({ estado: 'SC', disponivel: true }, 1, 1)
      ]);

      const total = totalResponse.totalCount ?? 0;
      const disponivel = availableResponse.totalCount ?? 0;
      const percentual = total > 0 ? Math.round((disponivel / total) * 10000) / 100 : 0;

      setScClusterCard({ total, disponivel, percentual });
    } catch (err) {
      // Se falhar, mantém fallback no resumo agregado atual
      console.error('Erro ao carregar métricas consistentes do cluster SC:', err);
    }
  }, []);

  const loadStockSummary = useCallback(async () => {
    try {
      const summary = await icCidService.getStockSummary();
      setStockSummary(summary);
      await loadScClusterCardMetrics();
    } catch (err) {
      console.error('Erro ao carregar resumo do estoque:', err);
    }
  }, [loadScClusterCardMetrics, summaryReloadKey]);

  useEffect(() => {
    loadICcIds();
  }, [loadICcIds]);

  useEffect(() => {
    loadStockSummary();
  }, [loadStockSummary]);

  /** Constrói estadosInfo com cluster Senior (PR: ER/MH; HB/CP: em todos os UFs da linha). */
  const buildEstadosInfo = (): { estado: string; cluster?: string | null }[] => {
    const cluster = ((formData as any).cluster as string | undefined)?.trim().toUpperCase();
    const clusterAll =
      cluster === 'HB' ||
      cluster === 'CP' ||
      cluster === 'TPCL' ||
      cluster === 'BA' ||
      cluster === 'HDM2' ||
      cluster === 'DH'
        ? cluster
        : undefined;
    return (formData.estados || []).map(estado => ({
      estado,
      cluster: clusterAll ?? (estado === 'PR' ? cluster || null : null),
    }));
  };

  const handleCreate = async () => {
    try {
      if (!formData.ccId || !formData.estados || formData.estados.length === 0) {
        showError('CCID e pelo menos um Estado são obrigatórios');
        return;
      }
      if (formData.estados.includes('PR') && !(formData as any).cluster) {
        showError('O campo Cluster EAS/TIM é obrigatório para o estado PR');
        return;
      }

      const payload: CreateICcIdRequest = {
        ...(formData as CreateICcIdRequest),
        estadosInfo: buildEstadosInfo(),
        isEsim: (formData as any).isEsim ?? false,
      };
      await icCidService.createICcId(payload);
      showSuccess('ICCID criado com sucesso!');
      setModalOpen(false);
      setFormData({});
      refreshAll();
    } catch (err: any) {
      showError(err.message || 'Erro ao criar ICCID');
    }
  };

  const handleUpdate = async () => {
    try {
      if (!selectedICcId) return;

      const payload: UpdateICcIdRequest = {
        ...(formData as UpdateICcIdRequest),
        estadosInfo: formData.estados && formData.estados.length > 0 ? buildEstadosInfo() : undefined,
        isEsim: (formData as any).isEsim,
      };
      await icCidService.updateICcId(selectedICcId.id, payload);
      showSuccess('ICCID atualizado com sucesso!');
      setModalOpen(false);
      setSelectedICcId(null);
      setFormData({});
      refreshAll();
    } catch (err: any) {
      showError(err.message || 'Erro ao atualizar ICCID');
    }
  };

  const handleDelete = async () => {
    try {
      if (!iccidToDelete) return;

      await icCidService.softDeleteICcId(iccidToDelete);
      showSuccess('ICCID removido com sucesso!');
      setDeleteConfirmOpen(false);
      setICcIdToDelete(null);
      refreshAll();
    } catch (err: any) {
      showError('Erro ao remover ICCID');
    }
  };

  const handleToggleAvailability = async (iccid: ICcIdResponse) => {
    try {
      if (iccid.disponivel) {
        const motivo = window.prompt('Motivo da indisponibilidade (opcional):');
        if (motivo === null) return;
        await icCidService.makeICcIdUnavailable(iccid.id, motivo || undefined);
        showSuccess('ICCID indisponibilizado com sucesso!');
      } else {
        // Se ICCID está indisponível e tem contrato vinculado, mostrar aviso
        if (!iccid.disponivel && iccid.contractId) {
          const confirmed = window.confirm(
            'ATENÇÃO: Este ICCID está vinculado a um contrato.\n\n' +
            'Ao liberar este ICCID:\n' +
            '- A ligação com o contrato será removida\n' +
            '- O ICCID ficará disponível novamente\n' +
            '- O contrato permanecerá ativo (deleção automática desativada temporariamente)\n\n' +
            'Deseja continuar?'
          );
          
          if (!confirmed) return;
        }
        
        await icCidService.makeICcIdAvailable(iccid.id);
        showSuccess('ICCID disponibilizado com sucesso!');
      }
      refreshAll();
    } catch (err: any) {
      showError('Erro ao alterar disponibilidade');
    }
  };

  const handleExcelUpload = async () => {
    try {
      if (!excelFile || !excelEstados || excelEstados.length === 0) {
        showError('Arquivo e pelo menos um Estado são obrigatórios');
        return;
      }

      setUploadingExcel(true);
      
      // Converter array de estados para string separada por vírgula
      const estadosString = excelEstados.join(',');
      const clusterParaEnvio =
        excelEstados.includes('PR') && excelClusterParana ? excelClusterParana : undefined;
      const result = await icCidService.createICcIdsFromExcel(excelFile, estadosString, clusterParaEnvio);
      
      // Filtrar mensagens de DEBUG do backend (não são erros reais)
      const mensagensReais = result.mensagensErro?.filter(msg => !msg.startsWith('DEBUG:')) || [];
      const errosReais = mensagensReais.length;
      
      // Atualizar resultado com erros reais
      const resultadoFiltrado = {
        ...result,
        erros: errosReais,
        mensagensErro: mensagensReais
      };
      
      setExcelResult(resultadoFiltrado);
      
      if (result.sucessos > 0) {
        showSuccess(`${result.sucessos} ICCIDs criados com sucesso!`);
        refreshAll();
      }
      
      if (errosReais > 0) {
        showError(`${errosReais} erros encontrados. Verifique os detalhes.`);
      }
    } catch (err: any) {
      showError('Erro ao processar arquivo Excel');
    } finally {
      setUploadingExcel(false);
    }
  };

  const handleOpenFastchipModal = () => {
    if (!isAdmin) {
      showError('Apenas administradores podem enviar o Excel de atualização FastChip.');
      return;
    }

    setFastchipModalOpen(true);
  };

  const handleFastchipUpload = async () => {
    if (!isAdmin) {
      showError('Apenas administradores podem executar atualização FastChip em massa.');
      return;
    }

    if (!fastchipFile) {
      showError('Selecione um arquivo Excel para continuar.');
      return;
    }

    try {
      setUploadingFastchip(true);
      const result = await icCidService.uploadFastchipExcel(fastchipFile);
      setFastchipResult(result);

      if (result.cancelledContracts > 0) {
        showSuccess(`${result.cancelledContracts} contrato(s) FastChip cancelado(s) com sucesso.`);
      } else {
        showSuccess('Processamento concluído sem cancelamentos.');
      }

      if (result.errors > 0) {
        showError(`Processamento finalizado com ${result.errors} erro(s).`);
      }

      refreshAll();
    } catch (err: any) {
      if (err?.response?.status === 403) {
        showError('Sem permissão: apenas administradores podem executar esta operação.');
      } else {
        const apiDetail =
          err?.response?.data?.errors?.['']?.[0] ||
          err?.response?.data?.title ||
          err?.response?.data?.message ||
          err?.message;
        showError(
          apiDetail
            ? `Erro ao processar o Excel de atualização FastChip: ${apiDetail}`
            : 'Erro ao processar o Excel de atualização FastChip.'
        );
      }
    } finally {
      setUploadingFastchip(false);
    }
  };

  // === Multi-seleção ===

  const selectableIcCids = useMemo(
    () => icCids.filter(ic => !ic.contractId),
    [icCids]
  );

  const allSelectableSelected = selectableIcCids.length > 0 && selectableIcCids.every(ic => selectedIds.has(ic.id));

  const handleToggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleToggleSelectAll = () => {
    if (allSelectableSelected) {
      setSelectedIds(prev => {
        const next = new Set(prev);
        selectableIcCids.forEach(ic => next.delete(ic.id));
        return next;
      });
    } else {
      setSelectedIds(prev => {
        const next = new Set(prev);
        selectableIcCids.forEach(ic => next.add(ic.id));
        return next;
      });
    }
  };

  const openBatchConfirm = (action: 'available' | 'unavailable' | 'delete') => {
    setBatchAction(action);
    setBatchConfirmOpen(true);
  };

  const handleBatchAction = async () => {
    if (!batchAction || selectedIds.size === 0) return;
    setBatchLoading(true);
    try {
      const ids = Array.from(selectedIds);
      let result: ICcIdBatchResultResponse;

      switch (batchAction) {
        case 'available':
          result = await icCidService.makeAvailableBatch(ids);
          break;
        case 'unavailable':
          result = await icCidService.makeUnavailableBatch(ids);
          break;
        case 'delete':
          result = await icCidService.softDeleteBatch(ids);
          break;
      }

      if (result.sucessos > 0) {
        showSuccess(`${result.sucessos} de ${result.totalProcessados} ICCIDs processados com sucesso!`);
      }
      if (result.erros > 0) {
        showError(`${result.erros} erros: ${result.mensagensErro.slice(0, 3).join('; ')}`);
      }

      setSelectedIds(new Set());
      refreshAll();
    } catch (err: any) {
      showError(err.message || 'Erro na operação em lote');
    } finally {
      setBatchLoading(false);
      setBatchConfirmOpen(false);
      setBatchAction(null);
    }
  };

  const openBatchEditModal = () => {
    setBatchEditMode('replace');
    setBatchEditEstados([]);
    setBatchEditCluster('');
    setBatchEditOpen(true);
  };

  const closeBatchEditModal = () => {
    if (batchEditLoading) return;
    setBatchEditOpen(false);
  };

  const handleBatchEditConfirm = async () => {
    if (selectedIds.size === 0) return;

    // Validações mínimas conforme modo
    if ((batchEditMode === 'replace' || batchEditMode === 'remove' || batchEditMode === 'add') && batchEditEstados.length === 0) {
      showError('Selecione pelo menos uma UF para o modo escolhido.');
      return;
    }
    if (batchEditMode === 'cluster-only' && batchEditCluster === '' && batchEditCluster !== '') {
      // (no-op) mantido por simetria; cluster vazio é permitido para "limpar"
    }

    const clusterValue = batchEditCluster.trim().toUpperCase();
    // Cluster por UF: aplica o cluster global a cada UF do payload nos modos que carregam UFs.
    const estadosInfo =
      batchEditMode === 'cluster-only' && batchEditEstados.length === 0
        ? undefined
        : batchEditEstados.map((estado) => ({
            estado,
            cluster: clusterValue ? clusterValue : null,
          }));

    const payload: BatchUpdateICcIdsRequest = {
      ids: Array.from(selectedIds),
      mode: batchEditMode,
      estadosInfo,
      // Envia cluster string vazia = limpar; envia valor para sobrescrever.
      cluster: batchEditCluster,
    };

    setBatchEditLoading(true);
    try {
      const result = await icCidService.batchUpdate(payload);

      if (result.sucessos > 0) {
        showSuccess(`${result.sucessos} de ${result.totalProcessados} ICCIDs atualizados em lote (batchId ${result.batchId}).`);
      }
      if (result.erros > 0) {
        showError(`${result.erros} erro(s): ${result.mensagensErro.slice(0, 3).join('; ')}`);
      }

      setSelectedIds(new Set());
      setBatchEditOpen(false);
      refreshAll();
    } catch (err: any) {
      showError(err?.message || 'Erro ao executar edição em lote.');
    } finally {
      setBatchEditLoading(false);
    }
  };

  const getBatchActionLabel = () => {
    switch (batchAction) {
      case 'available': return 'disponibilizar';
      case 'unavailable': return 'indisponibilizar';
      case 'delete': return 'remover';
      default: return '';
    }
  };

  // === Multi-search de CCID (busca parcial no banco ao digitar) ===

  const handleCcIdSearchChange = (_e: any, { searchQuery }: any) => {
    const trimmed = typeof searchQuery === 'string' ? searchQuery.trim() : '';
    if (ccIdSearchDebounceRef.current) {
      clearTimeout(ccIdSearchDebounceRef.current);
      ccIdSearchDebounceRef.current = null;
    }
    if (trimmed) {
      const exists = ccIdSearchOptions.some(o => o.value === trimmed);
      if (!exists) {
        setCcIdSearchOptions(prev => [...prev, { key: trimmed, value: trimmed, text: trimmed }]);
      }
      ccIdSearchDebounceRef.current = setTimeout(() => {
        setCcIdPartialQuery(trimmed);
        setCurrentPage(1);
        ccIdSearchDebounceRef.current = null;
      }, 350);
    } else {
      setCcIdPartialQuery('');
      setCurrentPage(1);
    }
  };

  const handleCcIdSearchSelect = (_e: any, { value }: any) => {
    const values = value as string[];
    setCcIdSearchValues(values);

    if (values.length === 0) {
      setFilters(prev => {
        const { ccId, ccIds, ...rest } = prev;
        return rest;
      });
    } else if (values.length === 1) {
      setFilters(prev => {
        const { ccIds, ...rest } = prev;
        return { ...rest, ccId: values[0] };
      });
    } else {
      setFilters(prev => {
        const { ccId, ...rest } = prev;
        return { ...rest, ccIds: values.join(',') };
      });
    }
    setCurrentPage(1);
  };

  const openCreateModal = () => {
    setFormData({ disponivel: true, estados: [] });
    setIsEditMode(false);
    setModalOpen(true);
  };

  const openEditModal = (iccid: ICcIdResponse) => {
    setSelectedICcId(iccid);
    const estadosInfo =
      iccid.estadosInfo && iccid.estadosInfo.length > 0
        ? iccid.estadosInfo
        : (iccid.estados || []).map((estado) => ({ estado, cluster: null as string | null }));
    const prCluster = estadosInfo.find((e) => e.estado === 'PR')?.cluster;
    const sharedCluster = estadosInfo.find((e) => e.cluster)?.cluster;
    const cluster =
      prCluster ||
      (sharedCluster === 'HB' ||
      sharedCluster === 'CP' ||
      sharedCluster === 'TPCL' ||
      sharedCluster === 'BA' ||
      sharedCluster === 'HDM2' ||
      sharedCluster === 'DH'
        ? sharedCluster
        : undefined);
    setFormData({
      ccId: iccid.ccId,
      estados: iccid.estados || [],
      disponivel: iccid.disponivel,
      contractId: iccid.contractId,
      isEsim: iccid.isEsim,
      ...(cluster ? { cluster } : {}),
    });
    setIsEditMode(true);
    setModalOpen(true);
  };

  const openDeleteConfirm = (iccid: ICcIdResponse) => {
    setICcIdToDelete(iccid.id);
    setDeleteConfirmOpen(true);
  };

  const handleFilterChange = (field: keyof ICcIdFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({});
    setCcIdSearchValues([]);
    setCcIdSearchOptions([]);
    setCcIdPartialQuery('');
    setSelectedIds(new Set());
    setCurrentPage(1);
  };

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortDescending(!sortDescending);
    } else {
      setSortBy(column);
      setSortDescending(true);
    }
  };

  // Estados que realmente usamos nas operações
  // Baseado nas regras do ContractsPage (getValidDdds e useAvailableICcIdsByState)
  const estadosOperacionais = [
    { key: 'SP', value: 'SP', text: 'SP - São Paulo' },
    { key: 'RJ', value: 'RJ', text: 'RJ - Rio de Janeiro' },
    { key: 'MG', value: 'MG', text: 'MG - Minas Gerais' },
    { key: 'SC', value: 'SC', text: 'SC - Santa Catarina' },
    { key: 'PR', value: 'PR', text: 'PR - Paraná' },
    { key: 'RS', value: 'RS', text: 'RS - Rio Grande do Sul' },
    { key: 'ES', value: 'ES', text: 'ES - Espírito Santo' },
    { key: 'BA', value: 'BA', text: 'BA - Bahia' },
    { key: 'PE', value: 'PE', text: 'PE - Pernambuco' },
    { key: 'CE', value: 'CE', text: 'CE - Ceará' },
    { key: 'PA', value: 'PA', text: 'PA - Pará' },
    { key: 'MA', value: 'MA', text: 'MA - Maranhão' },
    { key: 'AM', value: 'AM', text: 'AM - Amazonas' },
    { key: 'RN', value: 'RN', text: 'RN - Rio Grande do Norte' },
    { key: 'PB', value: 'PB', text: 'PB - Paraíba' },
    { key: 'AL', value: 'AL', text: 'AL - Alagoas' },
    { key: 'SE', value: 'SE', text: 'SE - Sergipe' },
    { key: 'PI', value: 'PI', text: 'PI - Piauí' },
    { key: 'RO', value: 'RO', text: 'RO - Rondônia' },
    { key: 'TO', value: 'TO', text: 'TO - Tocantins' }
  ];

  const getStatusColor = (disponivel: boolean) => {
    return disponivel ? 'green' : 'red';
  };

  const getStatusText = (disponivel: boolean) => {
    return disponivel ? 'Disponível' : 'Indisponível';
  };

  if (loading && icCids.length === 0) {
    return (
      <GlassPageWrapper>
        <Dimmer active inverted>
          <Loader size="large">Carregando ICCIDs...</Loader>
        </Dimmer>
      </GlassPageWrapper>
    );
  }

  return (
    <GlassPageWrapper title="Gestão de ICCIDs" icon="microchip">

      {/* Cards de ICCIDs por Cluster */}
      {stockSummary && (
        <StockClusterCards
          clusterSummary={clusterSummary}
          scClusterCard={scClusterCard}
          paranaFastBreakdown={stockSummary.paranaFastBreakdown ?? undefined}
          hbFastBreakdown={stockSummary.hbFastBreakdown ?? undefined}
          cpFastBreakdown={stockSummary.cpFastBreakdown ?? undefined}
          tpclFastBreakdown={stockSummary.tpclFastBreakdown ?? undefined}
          baFastBreakdown={stockSummary.baFastBreakdown ?? undefined}
          hdm2FastBreakdown={stockSummary.hdm2FastBreakdown ?? undefined}
          dhFastBreakdown={stockSummary.dhFastBreakdown ?? undefined}
          paranaAggregatedFallback={paranaAggregatedFallback}
        />
      )}

      {/* Filtros */}
      <Segment raised style={{ borderRadius: 12, padding: '1.25rem' }}>
        <Header as="h4" style={{ marginBottom: '1rem' }}>Filtros</Header>
        <Form>
          <Grid>
            <Grid.Row>
              <Grid.Column width={4}>
                <Form.Field>
                  <label>CCID(s)</label>
                  <Dropdown
                    placeholder="Digite ou cole CCIDs para buscar..."
                    multiple
                    search
                    selection
                    allowAdditions
                    clearable
                    options={ccIdSearchOptions}
                    value={ccIdSearchValues}
                    onSearchChange={handleCcIdSearchChange}
                    onChange={handleCcIdSearchSelect}
                    noResultsMessage="Digite um CCID e pressione Enter"
                    additionLabel="Buscar: "
                  />
                </Form.Field>
              </Grid.Column>
              <Grid.Column width={4}>
                <Form.Field>
                  <label>Estados</label>
                  <Dropdown
                    placeholder="Filtrar por Estados (múltipla seleção)"
                    selection
                    multiple
                    search
                    clearable
                    options={estadosOperacionais}
                    value={filters.estado?.split(',').filter(e => e.trim()) || []}
                    onChange={(e, { value }) => {
                      const estadosArray = value as string[];
                      handleFilterChange('estado', estadosArray.length > 0 ? estadosArray.join(',') : undefined);
                    }}
                  />
                </Form.Field>
              </Grid.Column>
              <Grid.Column width={3}>
                <Form.Field>
                  <label>Disponibilidade</label>
                  <Dropdown
                    placeholder="Filtrar por Disponibilidade"
                    selection
                    clearable
                    options={[
                      { key: 'true', value: true, text: 'Disponível' },
                      { key: 'false', value: false, text: 'Indisponível' }
                    ]}
                    value={filters.disponivel}
                    onChange={(e, { value }) => handleFilterChange('disponivel', value)}
                  />
                </Form.Field>
              </Grid.Column>
              <Grid.Column width={3}>
                <Form.Field>
                  <label>Cluster</label>
                  <Dropdown
                    placeholder="Cluster / regional"
                    selection
                    clearable
                    search
                    options={[
                      // Clusters Senior (Estoque Senior FAST)
                      { key: 'ER', value: 'ER', text: 'ER (Paraná)' },
                      { key: 'MH', value: 'MH', text: 'MH (Paraná)' },
                      { key: 'HB', value: 'HB', text: 'HB (PE / CE)' },
                      { key: 'CP', value: 'CP', text: 'CP (PI/AL/RN/PB)' },
                      { key: 'TPCL', value: 'TPCL', text: 'TPCL (DF/GO/TO/MT/MS/AC/RO)' },
                      { key: 'BA', value: 'BA', text: 'BA (Bahia — DDD 71, 73, 74, 75, 77)' },
                      { key: 'HDM2', value: 'HDM2', text: 'HDM2 (RS — DDD 51, 52, 53, 54, 55)' },
                      { key: 'DH', value: 'DH', text: 'DH (RJ / MG / ES)' },
                      // Regionais sem cluster (UF + cluster NULL) — todas as UFs disponíveis
                      ...BRAZIL_UF_OPTIONS.map(uf => ({
                        key: `uf-${uf.value}`,
                        value: uf.value,
                        text: `Regional ${uf.value}`,
                      })),
                    ]}
                    value={filters.cluster}
                    onChange={(e, { value }) => handleFilterChange('cluster', value || undefined)}
                  />
                </Form.Field>
              </Grid.Column>
              <Grid.Column width={2}>
                <Form.Field>
                  <label>&nbsp;</label>
                  <Button fluid onClick={clearFilters} icon="refresh" content="Limpar" />
                </Form.Field>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column width={8}>
                <Form.Field>
                  <label>CNPJ do cliente (contrato)</label>
                  <Input
                    placeholder="Ex.: 12.345.678/0001-90 ou só dígitos"
                    value={filters.clientCnpj || ''}
                    onChange={(e) => handleFilterChange('clientCnpj', e.target.value.trim() || undefined)}
                    icon="building outline"
                  />
                </Form.Field>
              </Grid.Column>
              <Grid.Column width={8} verticalAlign="bottom">
                <div style={{ fontSize: '12px', color: DesignSystem.colors.neutral.gray.dark, paddingBottom: 6 }}>
                  Filtra ICCIDs cujo contrato vinculado tenha este CNPJ (busca parcial na API).
                </div>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column width={16}>
                <div style={{ fontSize: '0.85em', color: '#666', background: 'rgba(66, 133, 244, 0.08)', padding: '0.6rem 0.75rem', borderRadius: 8 }}>
                  <strong>Dica:</strong> Pool E.R.2 = UFs RJ/MG/ES (filtro de estados). Stock DH = filtro Cluster <strong>DH</strong> (tag Senior).
                </div>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Form>
      </Segment>

      {/* Ações */}
      <Segment raised style={{ borderRadius: 12, padding: '1rem 1.25rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <Header as="h4" style={{ margin: 0 }}>
            Ações
            <Header.Subheader>Gerenciamento rápido de ICCIDs e automações</Header.Subheader>
          </Header>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <Button
              primary
              icon="plus"
              content="Novo ICCID"
              onClick={openCreateModal}
              style={getDefaultButtonStyle()}
            />
            <Button
              icon="upload"
              content="Importar Excel"
              onClick={() => setExcelModalOpen(true)}
              style={getDefaultButtonStyle()}
            />
            {isAdmin && (
              <Button
                color="violet"
                icon="sync alternate"
                content="Atualizar Fasts em Massa"
                onClick={handleOpenFastchipModal}
                style={getDefaultButtonStyle()}
              />
            )}
            <Button
              icon="refresh"
              content="Atualizar"
              onClick={refreshAll}
              style={getDefaultButtonStyle()}
            />
          </div>
        </div>
      </Segment>

      {/* Barra de Ações em Lote */}
      {selectedIds.size > 0 && (
        <Message info style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
          <div>
            <Icon name="check square" />
            <strong>{selectedIds.size}</strong> ICCID(s) selecionado(s)
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <Button
              size="small"
              color="green"
              icon="unlock"
              content="Disponibilizar"
              onClick={() => openBatchConfirm('available')}
              loading={batchLoading}
            />
            <Button
              size="small"
              color="orange"
              icon="lock"
              content="Indisponibilizar"
              onClick={() => openBatchConfirm('unavailable')}
              loading={batchLoading}
            />
            <Button
              size="small"
              color="blue"
              icon="edit"
              content="Editar em lote"
              onClick={openBatchEditModal}
              loading={batchEditLoading}
            />
            {isAdmin && (
              <Button
                size="small"
                color="red"
                icon="trash"
                content="Remover"
                onClick={() => openBatchConfirm('delete')}
                loading={batchLoading}
              />
            )}
            <Button
              size="small"
              basic
              icon="cancel"
              content="Limpar seleção"
              onClick={() => setSelectedIds(new Set())}
            />
          </div>
        </Message>
      )}

      {/* Informação de Paginação */}
      {totalCount > 0 && (
        <Segment basic style={{ padding: '0.5rem 0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
            <div>
              <strong>Total de ICCIDs:</strong> {totalCount}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <div>
                Exibindo {((currentPage - 1) * pageSize) + 1} a {Math.min(currentPage * pageSize, totalCount)} de {totalCount} registros
              </div>
              <Dropdown
                selection
                compact
                options={[
                  { key: 10, value: 10, text: '10 por página' },
                  { key: 20, value: 20, text: '20 por página' },
                  { key: 50, value: 50, text: '50 por página' },
                  { key: 100, value: 100, text: '100 por página' }
                ]}
                value={pageSize}
                onChange={(e, { value }) => {
                  setPageSize(Number(value));
                  setCurrentPage(1); // Resetar para primeira página
                }}
              />
            </div>
          </div>
        </Segment>
      )}

      {/* Tabela */}
      {error && (
        <Message negative>
          <Message.Header>Erro</Message.Header>
          <p>{error}</p>
        </Message>
      )}

      <GlassTable celled striped>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell collapsing>
              <Checkbox
                checked={allSelectableSelected}
                indeterminate={selectedIds.size > 0 && !allSelectableSelected}
                onChange={handleToggleSelectAll}
              />
            </Table.HeaderCell>
            <Table.HeaderCell 
              sorted={sortBy === 'ccId' ? (sortDescending ? 'descending' : 'ascending') : undefined}
              onClick={() => handleSort('ccId')}
              style={{ cursor: 'pointer' }}
            >
              CCID
            </Table.HeaderCell>
            <Table.HeaderCell 
              sorted={sortBy === 'estado' ? (sortDescending ? 'descending' : 'ascending') : undefined}
              onClick={() => handleSort('estado')}
              style={{ cursor: 'pointer' }}
            >
              Estados
            </Table.HeaderCell>
            <Table.HeaderCell 
              sorted={sortBy === 'disponivel' ? (sortDescending ? 'descending' : 'ascending') : undefined}
              onClick={() => handleSort('disponivel')}
              style={{ cursor: 'pointer' }}
            >
              Status
            </Table.HeaderCell>
            <Table.HeaderCell>Motivo Indisp.</Table.HeaderCell>
            <Table.HeaderCell>Cliente / contrato</Table.HeaderCell>
            <Table.HeaderCell>Status do contrato</Table.HeaderCell>
            <Table.HeaderCell 
              sorted={sortBy === 'createdAt' ? (sortDescending ? 'descending' : 'ascending') : undefined}
              onClick={() => handleSort('createdAt')}
              style={{ cursor: 'pointer' }}
            >
              Criado em
            </Table.HeaderCell>
            <Table.HeaderCell>Atualizado em</Table.HeaderCell>
            <Table.HeaderCell>Atualizado por</Table.HeaderCell>
            <Table.HeaderCell>Ações</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {icCids.map((iccid) => (
            <Table.Row key={iccid.id} active={selectedIds.has(iccid.id)}>
              <Table.Cell collapsing>
                <Checkbox
                  checked={selectedIds.has(iccid.id)}
                  disabled={!!iccid.contractId}
                  onChange={() => handleToggleSelect(iccid.id)}
                  title={iccid.contractId ? 'ICCID vinculado a contrato' : undefined}
                />
              </Table.Cell>
              <Table.Cell>
                <code>{iccid.ccId}</code>
                {iccid.isEsim && (
                  <Label color="purple" size="mini" style={{ marginLeft: '6px' }}>e-SIM</Label>
                )}
              </Table.Cell>
              <Table.Cell>
                {(() => {
                  const estadosInfo = iccid.estadosInfo && iccid.estadosInfo.length > 0
                    ? iccid.estadosInfo
                    : (iccid.estados && iccid.estados.length > 0
                        ? iccid.estados.map(e => ({ estado: e, cluster: null }))
                        : iccid.estado
                          ? [{ estado: iccid.estado, cluster: null }]
                          : []);

                  return estadosInfo.length > 0 ? (
                    estadosInfo.map((ei, index) => (
                      <span key={index} style={{ marginRight: '4px', marginBottom: '4px', display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
                        <Label color="blue">{ei.estado}</Label>
                        {ei.cluster && (
                          <Label color="teal" size="mini">{ei.cluster}</Label>
                        )}
                      </span>
                    ))
                  ) : (
                    <span>-</span>
                  );
                })()}
              </Table.Cell>
              <Table.Cell>
                <Label color={getStatusColor(iccid.disponivel)}>
                  {getStatusText(iccid.disponivel)}
                </Label>
              </Table.Cell>
              <Table.Cell>
                {iccid.motivoIndisponibilidade ? (
                  <span>
                    {iccid.motivoIndisponibilidade}
                    {iccid.dataIndisponibilidade && (
                      <div style={{ fontSize: '0.85em', color: '#888', marginTop: '2px' }}>
                        {new Date(iccid.dataIndisponibilidade).toLocaleDateString('pt-BR')}
                      </div>
                    )}
                  </span>
                ) : '-'}
              </Table.Cell>
              <Table.Cell>
                {iccid.contractId ? (
                  <div>
                    {iccid.clientName ? (
                      <div style={{ fontWeight: 600, color: DesignSystem.colors.neutral.black }}>{iccid.clientName}</div>
                    ) : null}
                    {iccid.clientCnpj ? (
                      <div style={{ fontSize: '13px', color: DesignSystem.colors.neutral.gray.darker, marginTop: 2 }}>
                        {formatCnpj(iccid.clientCnpj)}
                      </div>
                    ) : null}
                    {iccid.contractNumber ? (
                      <div style={{ fontSize: '11px', color: DesignSystem.colors.neutral.gray.dark, marginTop: 4 }}>
                        ID interno: {iccid.contractNumber}
                      </div>
                    ) : null}
                    {!iccid.clientName && !iccid.clientCnpj && <span style={{ color: DesignSystem.colors.neutral.gray.dark }}>Contrato vinculado</span>}
                  </div>
                ) : (
                  <span style={{ color: DesignSystem.colors.neutral.gray.dark }}>—</span>
                )}
              </Table.Cell>
              <Table.Cell>
                {iccid.contractId ? (
                  <Label color={getContractStatusSemanticColor(iccid.contractStatus)}>
                    {getContractStatusText(iccid.contractStatus)}
                  </Label>
                ) : (
                  '—'
                )}
              </Table.Cell>
              <Table.Cell>
                {new Date(iccid.createdAt).toLocaleDateString('pt-BR')}
              </Table.Cell>
              <Table.Cell>
                {(() => {
                  const updatedAt = (iccid as any)?.updatedAt as string | undefined;
                  return updatedAt ? new Date(updatedAt).toLocaleString('pt-BR') : '-';
                })()}
              </Table.Cell>
              <Table.Cell>
                {(((iccid as any)?.updatedBy as string) || '-')}
              </Table.Cell>
              <Table.Cell>
                <Button.Group size="mini">
                  <Button
                    icon="history"
                    onClick={() => setHistoryMovementIccid(iccid)}
                    style={getButtonHoverEffects()}
                    title="Histórico de movimentos FAST (append-only)"
                  />
                  {iccid.contractId && (
                    <Button
                      icon="file text outline"
                      color="blue"
                      onClick={() => openContractSummaryModal(iccid)}
                      style={getButtonHoverEffects()}
                      title="Resumo do contrato (vinculado ao FAST)"
                    />
                  )}
                  <Button
                    icon="edit"
                    onClick={() => openEditModal(iccid)}
                    disabled={!!iccid.contractId}
                    style={getButtonHoverEffects()}
                    title={iccid.contractId ? "ICCID vinculado a um contrato" : "Editar ICCID"}
                  />
                  <Button
                    icon={iccid.disponivel ? "lock" : "unlock"}
                    onClick={() => handleToggleAvailability(iccid)}
                    color={iccid.disponivel ? "orange" : "green"}
                    disabled={!!iccid.contractId}
                    style={getButtonHoverEffects()}
                    title={iccid.contractId ? "ICCID vinculado a um contrato" : (iccid.disponivel ? "Indisponibilizar" : "Disponibilizar")}
                  />
                  {isAdmin && (
                    <Button
                      icon="trash"
                      color="red"
                      onClick={() => openDeleteConfirm(iccid)}
                      disabled={!!iccid.contractId}
                      style={getButtonHoverEffects()}
                      title={iccid.contractId ? "ICCID vinculado a um contrato" : "Remover ICCID"}
                    />
                  )}
                </Button.Group>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </GlassTable>

      {/* Paginação */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '2rem', marginBottom: '2rem' }}>
          <Pagination
            activePage={currentPage}
            totalPages={totalPages}
            onPageChange={(e, { activePage }) => setCurrentPage(Number(activePage))}
            firstItem={{ content: <Icon name='angle double left' />, icon: true }}
            lastItem={{ content: <Icon name='angle double right' />, icon: true }}
            prevItem={{ content: <Icon name='angle left' />, icon: true }}
            nextItem={{ content: <Icon name='angle right' />, icon: true }}
            pointing
            secondary
          />
        </div>
      )}
      
      {/* Informação adicional quando há uma página */}
      {totalPages === 1 && totalCount > 0 && (
        <div style={{ textAlign: 'center', marginTop: '1rem', color: '#666' }}>
          Todos os {totalCount} registros estão sendo exibidos
        </div>
      )}

      {/* Modal de Criação/Edição */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} size="small">
        <Modal.Header>
          {isEditMode ? 'Editar ICCID' : 'Novo ICCID'}
        </Modal.Header>
        <Modal.Content>
          <Form>
            <Form.Field required>
              <label>CCID</label>
              <Input
                placeholder="Digite o CCID (20 caracteres)"
                value={formData.ccId || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, ccId: e.target.value }))}
                maxLength={20}
                disabled={isEditMode}
              />
            </Form.Field>
            <Form.Field required>
              <label>Estados (selecione um ou mais)</label>
              <Dropdown
                placeholder="Selecione os Estados"
                selection
                multiple
                search
                options={BRAZIL_UF_OPTIONS}
                value={formData.estados || []}
                onChange={(e, { value }) => setFormData(prev => ({ ...prev, estados: value as string[] }))}
              />
            </Form.Field>
            <Form.Field>
              <Checkbox
                label="Disponível"
                checked={formData.disponivel || false}
                onChange={(e, { checked }) => setFormData(prev => ({ ...prev, disponivel: checked }))}
              />
            </Form.Field>
            <Form.Field>
              <Checkbox
                label="e-SIM (chip virtual — ativado via QR Code)"
                checked={(formData as any).isEsim || false}
                onChange={(e, { checked }) => setFormData(prev => ({ ...prev, isEsim: checked }))}
              />
            </Form.Field>
            {(formData.estados || []).includes('PR') && (
              <Form.Field>
                <label>Cluster EAS/TIM (obrigatório para PR)</label>
                <Dropdown
                  placeholder="Selecione o Cluster"
                  selection
                  options={[
                    { key: 'ER', value: 'ER', text: 'ER' },
                    { key: 'MH', value: 'MH', text: 'MH' },
                  ]}
                  value={(formData as any).cluster || ''}
                  onChange={(e, { value }) => setFormData(prev => ({ ...prev, cluster: value as string }))}
                />
              </Form.Field>
            )}
            {(formData.estados || []).some((e) => e === 'PE' || e === 'CE') && (
              <Form.Field>
                <label>Cluster Senior HB (PE / CE)</label>
                <Dropdown
                  placeholder="HB ou use a coluna CLUSTER na planilha"
                  selection
                  clearable
                  options={[{ key: 'HB', value: 'HB', text: 'HB — DDDs 81, 87, 85, 88' }]}
                  value={(formData as any).cluster || ''}
                  onChange={(e, { value }) => setFormData(prev => ({ ...prev, cluster: (value as string) || '' }))}
                />
              </Form.Field>
            )}
            {(formData.estados || []).some((e) => ['PI', 'AL', 'RN', 'PB'].includes(e)) && (
              <Form.Field>
                <label>Cluster Senior CP (PI/AL/RN/PB)</label>
                <Dropdown
                  placeholder="CP ou use a coluna CLUSTER na planilha"
                  selection
                  clearable
                  options={[{ key: 'CP', value: 'CP', text: 'CP — DDDs 86, 89, 82, 84, 83' }]}
                  value={(formData as any).cluster || ''}
                  onChange={(e, { value }) => setFormData(prev => ({ ...prev, cluster: (value as string) || '' }))}
                />
              </Form.Field>
            )}
            {(formData.estados || []).some((e) =>
              ['DF', 'GO', 'TO', 'MT', 'MS', 'AC', 'RO'].includes(e)
            ) && (
              <Form.Field>
                <label>Cluster Senior TPCL (DF/GO/TO/MT/MS/AC/RO)</label>
                <Dropdown
                  placeholder="TPCL ou use a coluna CLUSTER na planilha"
                  selection
                  clearable
                  options={[
                    {
                      key: 'TPCL',
                      value: 'TPCL',
                      text: 'TPCL — DDDs 61 a 69 (DF / GO / TO / MT / MS / AC / RO)',
                    },
                  ]}
                  value={(formData as any).cluster || ''}
                  onChange={(e, { value }) =>
                    setFormData((prev) => ({ ...prev, cluster: (value as string) || '' }))
                  }
                />
              </Form.Field>
            )}
            {(formData.estados || []).includes('BA') && (
              <Form.Field>
                <label>Cluster Senior BA (Bahia)</label>
                <Dropdown
                  placeholder="BA ou use a coluna CLUSTER na planilha"
                  selection
                  clearable
                  options={[
                    {
                      key: 'BA',
                      value: 'BA',
                      text: 'BA — DDDs 71, 73, 74, 75, 77 (Salvador, Feira de Santana, Vitória da Conquista…)',
                    },
                  ]}
                  value={(formData as any).cluster || ''}
                  onChange={(e, { value }) =>
                    setFormData((prev) => ({ ...prev, cluster: (value as string) || '' }))
                  }
                />
                <p style={{ marginTop: 6, fontSize: '12px', color: '#666' }}>
                  Cidades de referência: 71 — Salvador, Lauro de Freitas, Camaçari · 73 — Ilhéus, Itabuna, Jequié ·
                  74 — Juazeiro, Jacobina · 75 — Feira de Santana, Alagoinhas · 77 — Vitória da Conquista, Barreiras
                </p>
              </Form.Field>
            )}
            {(formData.estados || []).includes('RS') && (
              <Form.Field>
                <label>Cluster Senior HDM2 (Rio Grande do Sul)</label>
                <Dropdown
                  placeholder="HDM2 ou use a coluna CLUSTER na planilha"
                  selection
                  clearable
                  options={[
                    {
                      key: 'HDM2',
                      value: 'HDM2',
                      text: 'HDM2 — DDDs 51, 52, 53, 54, 55 (Porto Alegre, Pelotas, Santa Maria, Caxias do Sul…)',
                    },
                  ]}
                  value={(formData as any).cluster || ''}
                  onChange={(e, { value }) =>
                    setFormData((prev) => ({ ...prev, cluster: (value as string) || '' }))
                  }
                />
                <p style={{ marginTop: 6, fontSize: '12px', color: '#666' }}>
                  Cidades de referência: 51 — Porto Alegre, Canoas · 52 — Pelotas, Rio Grande · 53 — Santa Maria, Bagé ·
                  54 — Caxias do Sul, Passo Fundo · 55 — Santa Cruz do Sul, Lajeado
                </p>
              </Form.Field>
            )}
            {(formData.estados || []).some((e) => e === 'RJ' || e === 'MG' || e === 'ES') && (
              <Form.Field>
                <label>Cluster Senior DH (RJ / MG / ES)</label>
                <Dropdown
                  placeholder="DH ou use a coluna CLUSTER na planilha"
                  selection
                  clearable
                  options={[
                    {
                      key: 'DH',
                      value: 'DH',
                      text: 'DH — RJ / MG / ES (DDDs espelho E.R.2)',
                    },
                  ]}
                  value={(formData as any).cluster || ''}
                  onChange={(e, { value }) =>
                    setFormData((prev) => ({ ...prev, cluster: (value as string) || '' }))
                  }
                />
                <p style={{ marginTop: 6, fontSize: '12px', color: '#666' }}>
                  Tag DH em todas as UFs da linha (padrão HB/CP/BA/HDM2). DDDs provisórios = espelho E.R.2.
                </p>
              </Form.Field>
            )}
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setModalOpen(false)}>Cancelar</Button>
          <Button
            primary
            onClick={isEditMode ? handleUpdate : handleCreate}
            style={getDefaultButtonStyle()}
          >
            {isEditMode ? 'Atualizar' : 'Criar'}
          </Button>
        </Modal.Actions>
      </Modal>

      {/* Modal de Upload Excel */}
      <Modal open={excelModalOpen} onClose={() => { setExcelModalOpen(false); setExcelFile(null); setExcelEstados([]); setExcelClusterParana(''); setExcelResult(null); }} size="small">
        <Modal.Header>Importar ICCIDs do Excel</Modal.Header>
        <Modal.Content>
          <Message info size="small">
            <Message.Header>Modelo do arquivo Excel</Message.Header>
            <p>O arquivo deve ter 4 colunas: <strong>CCID</strong>, <strong>STATUS</strong>, <strong>E-SIM</strong> e <strong>CLUSTER</strong> (opcional).</p>
            <p>Na coluna E-SIM, preencha <code>SIM</code> para chips e-SIM ou deixe em branco para chips físicos.</p>
            <p>Coluna <strong>CLUSTER</strong>: <strong>ER</strong> / <strong>MH</strong> (PR), <strong>HB</strong> (DDDs 81, 87, 85, 88), <strong>CP</strong> (DDDs 86, 89, 82, 84, 83), <strong>TPCL</strong> (DDDs 61 a 69 — DF, GO, TO, MT, MS, AC, RO), <strong>BA</strong> (DDDs 71, 73, 74, 75, 77 — Bahia), <strong>HDM2</strong> (DDDs 51, 52, 53, 54, 55 — RS) ou <strong>DH</strong> (RJ / MG / ES). Para PR sem coluna, use o campo abaixo.</p>
            <p>Layout alternativo <strong>CCID | CLUSTER | STATUS | E-SIM</strong> (cluster na 2ª coluna) é detectado automaticamente.</p>
            <p>O modelo CSV usa <strong>ponto e vírgula (;)</strong> como separador para abrir corretamente no Excel em português (uma coluna por campo).</p>
            <Button
              size="mini"
              icon="download"
              content="Baixar modelo CSV"
              onClick={() => {
                const sep = ';';
                const bom = '\uFEFF';
                const csv =
                  `${bom}CCID${sep}STATUS${sep}E-SIM${sep}CLUSTER\n` +
                  `89550000000000000001${sep}DISPONIVEL${sep}${sep}ER\n` +
                  `89550000000000000002${sep}DISPONIVEL${sep}SIM${sep}MH\n` +
                  `89550000000000000003${sep}DISPONIVEL${sep}${sep}DH`;
                const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url; a.download = 'modelo-importacao-iccids.csv'; a.click();
                URL.revokeObjectURL(url);
              }}
              style={{ marginTop: '8px' }}
            />
          </Message>
          <Form>
            <Form.Field required>
              <label>Arquivo Excel</label>
              <Input
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) setExcelFile(file);
                }}
              />
            </Form.Field>
            <Form.Field required>
              <label>Estados (selecione um ou mais)</label>
              <Dropdown
                placeholder="Selecione os Estados"
                selection
                multiple
                search
                options={BRAZIL_UF_OPTIONS}
                value={excelEstados}
                onChange={(e, { value }) => {
                  const next = value as string[];
                  setExcelEstados(next);
                  if (!next.includes('PR')) setExcelClusterParana('');
                }}
              />
              <div style={{ marginTop: '8px', fontSize: '0.9em', color: '#666' }}>
                Os ICCIDs do arquivo Excel serão disponibilizados para todos os estados selecionados
              </div>
            </Form.Field>
            {excelEstados.includes('PR') && (
              <Form.Field>
                <label>Cluster EAS/TIM (PR) — opcional se a coluna CLUSTER da planilha estiver preenchida</label>
                <Dropdown
                  placeholder="ER ou MH (ou use só a coluna CLUSTER)"
                  selection
                  clearable
                  options={[
                    { key: 'ER', value: 'ER', text: 'ER' },
                    { key: 'MH', value: 'MH', text: 'MH' },
                  ]}
                  value={excelClusterParana || undefined}
                  onChange={(e, { value }) => setExcelClusterParana((value as string) || '')}
                />
              </Form.Field>
            )}
          </Form>
          
          {excelResult && (
            <Segment>
              <Header as="h4">Resultado da Importação</Header>
              <p><strong>Total Processados:</strong> {excelResult.totalProcessados}</p>
              <p><strong>Sucessos:</strong> {excelResult.sucessos}</p>
              <p><strong>Erros:</strong> {excelResult.erros}</p>
              
              {excelResult.mensagensErro.length > 0 && (
                <div>
                  <Header as="h5">Mensagens de Erro:</Header>
                  <List bulleted>
                    {excelResult.mensagensErro.map((msg, index) => (
                      <List.Item key={index}>{msg}</List.Item>
                    ))}
                  </List>
                </div>
              )}
            </Segment>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => { setExcelModalOpen(false); setExcelFile(null); setExcelEstados([]); setExcelClusterParana(''); setExcelResult(null); }}>Fechar</Button>
          <Button
            primary
            onClick={handleExcelUpload}
            loading={uploadingExcel}
            disabled={!excelFile || !excelEstados || excelEstados.length === 0 || uploadingExcel}
            style={getDefaultButtonStyle()}
          >
            Importar
          </Button>
        </Modal.Actions>
      </Modal>

      {/* Modal de Atualização FastChip em Massa */}
      <Modal
        open={fastchipModalOpen}
        onClose={() => setFastchipModalOpen(false)}
        size="small"
      >
        <Modal.Header>Atualizar Fasts em Massa (Automação)</Modal.Header>
        <Modal.Content>
          {!isAdmin && (
            <Message negative content="Apenas administradores podem executar esta operação." />
          )}

          <Message info size="small">
            <Message.Header>Formato do arquivo</Message.Header>
            <p>Este arquivo é exportado diretamente do sistema TIM/FAST. Exporte o relatório de ICCIDs disponíveis e faça upload aqui.</p>
            <p>Colunas esperadas: <strong>ICCID</strong>, <strong>STATUS_GERAL</strong>, <strong>STATUS_ATUAL</strong></p>
            <Button
              size="mini"
              icon="download"
              content="Baixar modelo CSV"
              onClick={() => {
                const sep = ';';
                const bom = '\uFEFF';
                const csv = `${bom}ICCID${sep}STATUS_GERAL${sep}STATUS_ATUAL\n89550000000000000001${sep}DISPONIVEL${sep}\n89550000000000000002${sep}DISPONIVEL${sep}`;
                const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url; a.download = 'modelo-atualizacao-fastchip.csv'; a.click();
                URL.revokeObjectURL(url);
              }}
              style={{ marginTop: '8px' }}
            />
          </Message>
          <Form>
            <Form.Field required>
              <label>Arquivo Excel da automação FastChip</label>
              <Input
                type="file"
                accept=".xlsx,.xls"
                disabled={!isAdmin || uploadingFastchip}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) setFastchipFile(file);
                }}
              />
              <div style={{ marginTop: '8px', fontSize: '0.9em', color: '#666' }}>
                O sistema analisará ICCIDs disponíveis e cancelará contratos elegíveis conforme regras de status/tempo.
              </div>
            </Form.Field>
          </Form>

          {fastchipResult && (
            <Segment>
              <Header as="h4">Resultado do Processamento</Header>
              <Grid columns={3} stackable>
                <Grid.Column><strong>Linhas lidas:</strong> {fastchipResult.totalRowsRead}</Grid.Column>
                <Grid.Column><strong>Disponíveis:</strong> {fastchipResult.availableRows}</Grid.Column>
                <Grid.Column><strong>ICCIDs distintos:</strong> {fastchipResult.distinctAvailableIccids}</Grid.Column>
                <Grid.Column><strong>Contratos encontrados:</strong> {fastchipResult.contractsFound}</Grid.Column>
                <Grid.Column><strong>Elegíveis:</strong> {fastchipResult.eligibleContracts}</Grid.Column>
                <Grid.Column><strong>Cancelados:</strong> {fastchipResult.cancelledContracts}</Grid.Column>
                <Grid.Column><strong>Ignorados:</strong> {fastchipResult.skippedContracts}</Grid.Column>
                <Grid.Column><strong>Não encontrados:</strong> {fastchipResult.notFoundIccids}</Grid.Column>
                <Grid.Column><strong>Erros:</strong> {fastchipResult.errors}</Grid.Column>
              </Grid>

              {fastchipResult.messages?.length > 0 && (
                <>
                  <Header as="h5" style={{ marginTop: '1rem' }}>Mensagens</Header>
                  <List bulleted>
                    {fastchipResult.messages.map((msg, index) => (
                      <List.Item key={`fastchip-msg-${index}`}>{msg}</List.Item>
                    ))}
                  </List>
                </>
              )}
            </Segment>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setFastchipModalOpen(false)}>Fechar</Button>
          <Button
            color="violet"
            onClick={handleFastchipUpload}
            loading={uploadingFastchip}
            disabled={!isAdmin || !fastchipFile || uploadingFastchip}
            style={getDefaultButtonStyle()}
          >
            Processar Excel
          </Button>
        </Modal.Actions>
      </Modal>

      <FastLinkedContractModal
        open={contractModalOpen}
        onClose={closeContractSummaryModal}
        focusContractId={contractModalIccid?.contractId ?? null}
        isAdmin={isAdmin}
        fastchipBatchBypassMovementSource="ICcIdsModal"
        historyIcCidControl={contractModalIccid}
        onAfterSwapSuccess={() => {
          refreshAll();
        }}
        enableAdminContractCancel
        onAfterCancelContract={() => {
          refreshAll();
        }}
      />

      <FastChipMovementHistoryModal
        iccid={historyMovementIccid}
        open={historyModalOpen}
        onClose={() => setHistoryMovementIccid(null)}
      />

      {/* Modal Editar selecionados em lote */}
      <Modal open={batchEditOpen} onClose={closeBatchEditModal} size="small">
        <Modal.Header>Editar {selectedIds.size} Fastchip(s) em lote</Modal.Header>
        <Modal.Content>
          <Message info size="small">
            <Message.Header>Como funciona</Message.Header>
            <List bulleted size="small">
              <List.Item><strong>Substituir</strong>: troca a lista de UFs do chip pela lista escolhida abaixo.</List.Item>
              <List.Item><strong>Adicionar UFs</strong>: une as UFs atuais com as escolhidas (cluster do payload sobrescreve, demais mantidos).</List.Item>
              <List.Item><strong>Remover UFs</strong>: tira as UFs escolhidas do conjunto atual (mantém demais e seus clusters).</List.Item>
              <List.Item><strong>Apenas cluster</strong>: mantém UFs atuais e troca o cluster — em todas (se nenhuma UF for marcada) ou apenas nas UFs marcadas.</List.Item>
              <List.Item>O backend NÃO valida cluster ↔ UF. Use com critério.</List.Item>
            </List>
          </Message>
          <Form>
            <Form.Field>
              <label>Modo</label>
              <Form.Group inline>
                <Form.Radio
                  label="Substituir"
                  value="replace"
                  checked={batchEditMode === 'replace'}
                  onChange={() => setBatchEditMode('replace')}
                />
                <Form.Radio
                  label="Adicionar UFs"
                  value="add"
                  checked={batchEditMode === 'add'}
                  onChange={() => setBatchEditMode('add')}
                />
                <Form.Radio
                  label="Remover UFs"
                  value="remove"
                  checked={batchEditMode === 'remove'}
                  onChange={() => setBatchEditMode('remove')}
                />
                <Form.Radio
                  label="Apenas cluster"
                  value="cluster-only"
                  checked={batchEditMode === 'cluster-only'}
                  onChange={() => setBatchEditMode('cluster-only')}
                />
              </Form.Group>
            </Form.Field>

            <Form.Field>
              <label>
                UFs {batchEditMode === 'cluster-only' ? '(opcional — vazio = todas as UFs atuais de cada chip)' : '(obrigatório)'}
              </label>
              <Dropdown
                placeholder="Selecione as UFs"
                selection
                multiple
                search
                clearable
                options={BRAZIL_UF_OPTIONS}
                value={batchEditEstados}
                onChange={(e, { value }) => setBatchEditEstados(value as string[])}
              />
            </Form.Field>

            <Form.Field>
              <label>
                Cluster Senior (opcional — em branco = sem cluster / limpar)
              </label>
              <Dropdown
                placeholder="ER / MH / HB / CP / TPCL / BA / HDM2 / DH ou vazio"
                selection
                clearable
                options={[
                  { key: 'ER', value: 'ER', text: 'ER (Paraná)' },
                  { key: 'MH', value: 'MH', text: 'MH (Paraná)' },
                  { key: 'HB', value: 'HB', text: 'HB (PE / CE)' },
                  { key: 'CP', value: 'CP', text: 'CP (PI/AL/RN/PB)' },
                  { key: 'TPCL', value: 'TPCL', text: 'TPCL (DF/GO/TO/MT/MS/AC/RO)' },
                  {
                    key: 'BA',
                    value: 'BA',
                    text: 'BA (Bahia — DDD 71, 73, 74, 75, 77)',
                  },
                  {
                    key: 'HDM2',
                    value: 'HDM2',
                    text: 'HDM2 (RS — DDD 51, 52, 53, 54, 55)',
                  },
                  {
                    key: 'DH',
                    value: 'DH',
                    text: 'DH (RJ / MG / ES)',
                  },
                ]}
                value={batchEditCluster || undefined}
                onChange={(e, { value }) => setBatchEditCluster((value as string) || '')}
              />
            </Form.Field>

            <Message
              size="small"
              content={`${selectedIds.size} chip(s) serão atualizados (modo: ${batchEditMode}).`}
            />
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={closeBatchEditModal} disabled={batchEditLoading}>Cancelar</Button>
          <Button
            primary
            onClick={handleBatchEditConfirm}
            loading={batchEditLoading}
            disabled={
              batchEditLoading ||
              selectedIds.size === 0 ||
              ((batchEditMode === 'replace' || batchEditMode === 'add' || batchEditMode === 'remove') && batchEditEstados.length === 0)
            }
            style={getDefaultButtonStyle()}
          >
            Confirmar
          </Button>
        </Modal.Actions>
      </Modal>

      {/* Confirmação de Exclusão */}
      <Confirm
        open={deleteConfirmOpen}
        onCancel={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDelete}
        content="Tem certeza que deseja remover este ICCID?"
        confirmButton="Sim, remover"
        cancelButton="Cancelar"
      />

      {/* Confirmação de Ação em Lote */}
      <Confirm
        open={batchConfirmOpen}
        onCancel={() => { setBatchConfirmOpen(false); setBatchAction(null); }}
        onConfirm={handleBatchAction}
        content={`Tem certeza que deseja ${getBatchActionLabel()} ${selectedIds.size} ICCID(s)?`}
        confirmButton={{ content: `Sim, ${getBatchActionLabel()}`, loading: batchLoading }}
        cancelButton="Cancelar"
      />
    </GlassPageWrapper>
  );
};

export default ICcIdsPage;
